import { Table } from 'antd';

/**
 * Novčane kolone pregleda obračuna — jedan izvor za sve poglede.
 *
 * Redosled je Danilov, doslovno sa sastanka 19.08.2026: „bruto, porez,
 * doprinose na teret zaposlenog, neto, obustave, neto za isplatu i bruto 2".
 * Isti set već izlazi u HLB Excel izvozu (`hlbExport.services.js`) — ekran ga je
 * do sada krio, pa je Danilo totale morao da čita iz Excela.
 *
 * Zašto ovde a ne u svakom ekranu: pregled obračuna ima dva prikaza (obični i
 * shadow), i oni su se već bili razišli — shadow je pokazivao samo bruto i neto.
 */
export const AMOUNT_COLS = [
  { key: 'gross', i18n: 'gross' },
  { key: 'tax', i18n: 'tax' },
  { key: 'contribEmployee', i18n: 'ozContribEmployee' },
  { key: 'net', i18n: 'net' },
  { key: 'deductions', i18n: 'ozDeductions' },
  { key: 'netPayout', i18n: 'ozNetPayout' },
  { key: 'contribEmployer', i18n: 'ozContribEmployer' },
  { key: 'grossTwo', i18n: 'ozGrossTwo' },
];

export function fmtAmount(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('sr-RS', { maximumFractionDigits: 2 });
}

/** Iznosi jedne stavke obračuna → ravna polja za red tabele. */
export function pickAmounts(calculation) {
  const c = calculation || {};
  return {
    gross: c.totalGross,
    tax: c.tax,
    contribEmployee: c.contributions?.employee?.total,
    contribEmployer: c.contributions?.employer?.total,
    net: c.netSalary,
    deductions: c.totalDeductions,
    // Za isplatu = neto − obustave. Kad obustava nema, motor ne upisuje
    // netAfterDeductions, pa se pada na neto.
    netPayout: c.netAfterDeductions != null ? c.netAfterDeductions : c.netSalary,
    grossTwo: c.totalEmployerCost,
  };
}

export function amountColumns(t) {
  return AMOUNT_COLS.map((c) => ({
    title: t(c.i18n, { ns: 'forms' }),
    dataIndex: c.key,
    key: c.key,
    align: 'right',
    render: fmtAmount,
    width: 130,
  }));
}

export function sumOf(rows, key) {
  return (rows || []).reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
}

/**
 * Zbirni red. Računa se nad redovima, ne nad stranicom — Danilo poredi totale sa
 * AB Kalkulacijom, a paginacija bi dala zbir jedne strane.
 *
 * `leadingCells` je broj praznih ćelija pre kolone sa oznakom (tabela sa
 * `expandable` ima jednu dodatnu kolonu na početku, obična nema).
 */
export function renderAmountSummary({
  t,
  rows,
  visibleRows,
  leadingCells = 0,
}) {
  const all = rows || [];
  const shown = visibleRows || all;
  const partial = shown.length !== all.length;

  const row = (label, data, strong) => (
    <Table.Summary.Row>
      {Array.from({ length: leadingCells }, (_, i) => (
        <Table.Summary.Cell key={`lead-${i}`} index={i} />
      ))}
      <Table.Summary.Cell index={leadingCells}>
        {strong ? <strong>{label}</strong> : label}
      </Table.Summary.Cell>
      {AMOUNT_COLS.map((c, i) => (
        <Table.Summary.Cell
          key={c.key}
          index={leadingCells + 1 + i}
          align="right"
        >
          {strong ? (
            <strong>{fmtAmount(sumOf(data, c.key))}</strong>
          ) : (
            fmtAmount(sumOf(data, c.key))
          )}
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );

  return (
    <Table.Summary fixed>
      {row(
        partial
          ? `${t('ozTotalShown', { ns: 'forms' })} (${shown.length})`
          : `${t('total', { ns: 'forms' })} (${all.length})`,
        shown,
        true
      )}
      {partial
        ? row(`${t('ozTotalAll', { ns: 'forms' })} (${all.length})`, all, false)
        : null}
    </Table.Summary>
  );
}
